import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Cable, Info, ExternalLink } from 'lucide-react'
import { api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { useToast } from '../components/Toast.jsx'
import { CardHead, SettingsRow, Switch } from '../components/SettingsLayout.jsx'
import Icon from '../components/Icon.jsx'
import claudeMark from '../assets/claude.webp'
import chatgptMark from '../assets/chatgpt.webp'
import smartsheetMark from '../assets/smartsheet.webp'

/**
 * Settings → Integrations: one tile per outside thing this company can be connected to.
 *
 * Three panes, and the split is about who is doing what rather than about what is being
 * looked at. The GRID is for everyone — it is the answer to "can this talk to anything". A
 * DETAIL pane is for the person connecting their own account. MANAGE is the owner's, and it
 * is the only place a switch appears.
 *
 * Everything is off until an owner turns it on, and an integration that is off is drawn
 * dimmed rather than hidden — "disabled, not hidden", the same rule the rest of the app
 * follows. Somebody who goes looking for this and finds nothing concludes the product cannot
 * do it; somebody who finds it greyed out learns who to ask.
 */

/**
 * The registry. One entry per integration, so adding the next one is a data edit and not a
 * rewrite of three components.
 *
 *   key         the URL segment, /settings/integrations/<key>
 *   setting     the field on PUT /api/admin/settings that turns it on
 *   enabled     read off the /api/me projection
 *   marks       the logos, on the tile and beside its headings — a person scans for the one
 *               they recognise long before they read the name
 *   comingSoon  built enough to name, not enough to use. It has no setting, no detail pane
 *               and nothing to manage; it is on the page so that "does this talk to
 *               Smartsheet" has an answer, and the answer is "not yet" rather than silence.
 */
export const INTEGRATIONS = [
  {
    key: 'mcp',
    name: 'MCP Integrations',
    blurb:
      'Connect an AI assistant to the board over the Model Context Protocol. AI assistants ' +
      'will take actions on your behalf.',
    setting: 'mcpEnabled',
    enabled: (integrations) => Boolean(integrations?.mcp?.enabled),
    marks: [
      { src: claudeMark, alt: 'Claude' },
      { src: chatgptMark, alt: 'ChatGPT' },
    ],
    // One walkthrough per assistant rather than one generic set of steps. They do put the
    // address in the same kind of place, but not under the same words, and a person half way
    // through setting this up on a Friday afternoon needs the words their own screen uses.
    walkthroughs: [
      {
        name: 'Claude',
        mark: claudeMark,
        // There is no deep link that installs a connector — the documented claude:// scheme
        // only opens a chat, a Code session or a Cowork session — so this is as close as it
        // gets: put the URL on the clipboard and open the dialog it has to be pasted into.
        // The connector list is shared across claude.ai, Desktop, mobile and Code, so adding
        // it here is adding it everywhere.
        //
        // The ?modal= parameter is undocumented, so treat it as best effort; without it the
        // link still lands on the Connectors page, which is where somebody needs to be.
        action: {
          label: 'Copy URL and open Claude',
          href: 'https://claude.ai/settings/connectors?modal=add-custom-connector',
        },
        steps: [
          'Paste the address into Remote MCP server URL.',
          'Leave the authentication settings as Claude detects them.',
          'Confirm, then sign in and allow the access it lists.',
        ],
        commandLabel: 'Or, in Claude Code',
        command: (url) => `claude mcp add --transport http crewdispatch ${url}`,
      },
      {
        name: 'ChatGPT',
        mark: chatgptMark,
        // Same best-effort shape as Claude's: a hash route into the Connectors pane. Neither
        // assistant documents a link that installs a connector, so the most either button
        // can do is carry the address on the clipboard to the screen it gets pasted into.
        action: {
          label: 'Copy URL and open ChatGPT',
          href: 'https://chatgpt.com/#settings/Connectors',
        },
        // The button already lands on the Connectors pane, so the steps start after it —
        // the same shape as Claude's beside it.
        steps: [
          'Choose Create, or Add custom connector.',
          'Paste the address as the server URL, and pick OAuth for authentication.',
          'Sign in when it asks, and allow the access it lists.',
        ],
      },
    ],
    Detail: McpDetail,
    Manage: McpManage,
  },
  {
    key: 'smartsheet',
    name: 'Smartsheet',
    blurb: 'Integrate with Smartsheet. This integration is one-way only.',
    comingSoon: true,
    marks: [{ src: smartsheetMark, alt: 'Smartsheet' }],
  },
]

const useIntegrations = () => {
  const { integrations } = useAuth()
  // `on` is the one thing every pane branches on, so it is resolved here rather than in
  // three components. Something not built yet is never on, whatever anybody sets.
  return INTEGRATIONS.map((i) => ({ ...i, on: !i.comingSoon && i.enabled(integrations) }))
}

// ── The grid ────────────────────────────────────────────────────────────────

export function IntegrationsHome() {
  const { can } = useAuth()
  const list = useIntegrations()

  return (
    <div className="card">
      <CardHead
        title="Integrations"
        sub="Outside services this company can be connected to."
        action={
          can.isOwner && (
            <Link className="btn ghost" to="/settings/integrations/manage">
              <Cable size={16} strokeWidth={2} /> Manage integrations
            </Link>
          )
        }
      />

      <div className="integration-tiles">
        {list.map((i) => (
          <IntegrationTile key={i.key} integration={i} />
        ))}
      </div>
    </div>
  )
}

function IntegrationTile({ integration: i }) {
  const navigate = useNavigate()
  const { can } = useAuth()
  return (
    <button
      type="button"
      className={`integration-tile ${i.on ? '' : 'off'}`}
      // Nothing to open: either it is switched off, or it does not exist yet. The tile
      // stays on the page saying which.
      disabled={!i.on}
      onClick={() => navigate(`/settings/integrations/${i.key}`)}
    >
      <Marks marks={i.marks} />
      <span className="integration-text">
        <span className="integration-name">
          {i.name}
          {i.comingSoon && <span className="tag muted">Coming soon</span>}
          {!i.comingSoon && !i.on && <span className="tag muted">Disabled</span>}
        </span>
        <span className="integration-blurb">
          {/* A dimmed tile that does not say why is a dead end. "Coming soon" is its own
              answer; for the rest, owners get the door and everybody else gets the name of
              the person to ask. */}
          {i.comingSoon
            ? `${i.blurb} Not available yet.`
            : i.on
              ? i.blurb
              : can.isOwner
                ? `${i.blurb} Turn it on under Manage integrations.`
                : `${i.blurb} An owner has not turned this on.`}
        </span>
      </span>
      {i.on && (
        <span className="integration-go" aria-hidden="true">
          <Icon name="right" size={16} strokeWidth={2.2} />
        </span>
      )}
    </button>
  )
}

/**
 * The assistant logos, overlapped so a row of them reads as one group rather than as a list
 * of separate things. `small` is the size that sits beside a heading.
 */
function Marks({ marks, small = false }) {
  return (
    <span className={`integration-marks ${small ? 'sm' : ''}`} aria-hidden="true">
      {marks.map((m) => (
        <img key={m.alt} src={m.src} alt="" title={m.alt} />
      ))}
    </span>
  )
}

/** Shown above a detail pane, so there is a way back that is not the browser button. */
function BackToIntegrations() {
  return (
    <Link className="settings-back" to="/settings/integrations">
      <Icon name="back" size={15} strokeWidth={2.2} /> Integrations
    </Link>
  )
}

// ── One integration's detail ────────────────────────────────────────────────

export function IntegrationDetail({ integrationKey }) {
  const list = useIntegrations()
  const found = list.find((i) => i.key === integrationKey)
  // A key that names nothing, or something switched off since the page was opened.
  if (!found || !found.on) return <IntegrationsHome />
  const { Detail } = found
  return (
    <>
      <BackToIntegrations />
      <Detail integration={found} />
    </>
  )
}

/**
 * MCP: the address to paste, and what is connected to this person's own account.
 *
 * No switch here — that is the owner's, under Manage. This pane is for the person doing the
 * connecting, and everything on it is theirs.
 */
function McpDetail({ integration }) {
  const { integrations } = useAuth()
  const url = integrations?.mcp?.url
  return (
    <>
      <ConnectCard url={url} integration={integration} />
      {/* This person's own, even for an owner. "What have I connected" is a different
          question from "what has anybody connected", and the second one belongs on the
          owner's Manage page rather than in the way of somebody setting theirs up. */}
      <Connections scope="mine" />
    </>
  )
}

/**
 * Copy to the clipboard, with the two-second tick every copy button in this pane shows.
 *
 * Shared rather than repeated because three of them now do it, and because the failure case
 * is the same everywhere: the clipboard can be refused outright (a permission, an insecure
 * origin), and the thing being copied is always already on screen to be read.
 */
function useCopy() {
  const toast = useToast()
  const [done, setDone] = useState(false)
  return {
    done,
    write: async (text) => {
      try {
        await navigator.clipboard.writeText(text)
        setDone(true)
        setTimeout(() => setDone(false), 2000)
      } catch {
        toast.info('Copy it by hand — the browser would not do it for us.')
      }
    },
  }
}

function ConnectCard({ url, integration }) {
  const { tenant } = useAuth()
  const copy = useCopy()

  return (
    <>
      <div className="card">
        <CardHead
          title={
            <span className="card-title-marks">
              <Marks marks={integration.marks} small />
              {integration.name}
            </span>
          }
          sub="Paste this address into your assistant. It is not a password: whoever signs in gets their own permissions and nobody else's."
        />
        <div className="settings-list">
          <SettingsRow
            label={`${tenant?.name}'s MCP URL`}
            hint={`This gives AI assistants access to ${tenant?.name} only. If you are part of more than one company, each needs its own address.`}
            stacked
          >
            <span className="connect-url">
              <span className="mono">{url}</span>
              <button className="btn sm" onClick={() => copy.write(url)}>
                <Icon name={copy.done ? 'check' : 'copy'} size={14} /> {copy.done ? 'Copied' : 'Copy'}
              </button>
            </span>
          </SettingsRow>
        </div>
      </div>

      <div className="card">
        <CardHead
          title="How to connect"
          sub="Both put the address in the same kind of place, under different words."
        />
        <div className="walkthroughs">
          {integration.walkthroughs.map((w) => (
            <Walkthrough key={w.name} walkthrough={w} url={url} />
          ))}
        </div>
      </div>
    </>
  )
}

function Walkthrough({ walkthrough: w, url }) {
  const copy = useCopy()
  return (
    <div className="walkthrough">
      <div className="walkthrough-head">
        <img src={w.mark} alt="" aria-hidden="true" />
        <h3>{w.name}</h3>
      </div>

      <ol className="connect-steps">
        {w.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>

      {w.command && <CommandRow label={w.commandLabel} command={w.command(url)} />}

      {/* Last, under everything it is an alternative to. */}
      {w.action && (
        <button
          className="btn sm walkthrough-action"
          onClick={async () => {
            // Clipboard first, then the tab: a popup blocker that stops the second must not
            // also lose the first, and the address is the half that matters.
            await copy.write(url)
            window.open(w.action.href, '_blank', 'noopener')
          }}
        >
          {/* Trailing, the way an "opens elsewhere" glyph conventionally sits, and in the
              same slot in both states so the button does not reflow on click. */}
          <span>{copy.done ? 'Copied — paste it in Claude' : w.action.label}</span>
          {copy.done ? <Icon name="check" size={14} /> : <ExternalLink size={14} strokeWidth={2} />}
        </button>
      )}
    </div>
  )
}

/**
 * The command, as a button rather than as text.
 *
 * Nobody retypes a line with a URL in it, so the string itself is doing no work on screen —
 * it is only length, and it wraps to three lines and pushes everything below it down. The
 * button is the whole affordance.
 */
function CommandRow({ label, command }) {
  const copy = useCopy()
  return (
    <div className="walkthrough-command">
      <span className="hint">{label}</span>
      <button className="btn sm" onClick={() => copy.write(command)}>
        <Icon name={copy.done ? 'check' : 'copy'} size={13} />
        {copy.done ? 'Copied' : 'Copy command'}
      </button>
    </div>
  )
}

/**
 * @param scope 'mine' — this person's own connections; 'tenant' — the whole company's.
 *   The server decides which it will actually return, from the caller's own roles; this
 *   only chooses the wording.
 */
function Connections({ scope, onEmpty = null }) {
  const toast = useToast()
  const qc = useQueryClient()
  const tenantWide = scope === 'tenant'
  const { data } = useQuery({
    // Keyed by scope, so the owner's two views do not overwrite each other in the cache.
    // Invalidations name the prefix and clear both.
    queryKey: ['oauth-connections', scope],
    queryFn: () => api.get(`/api/oauth/connections${tenantWide ? '' : '?scope=mine'}`),
  })
  const rows = data?.connections || []

  async function disconnect(row) {
    const who = tenantWide && row.userName ? ` for ${row.userName}` : ''
    if (!window.confirm(`Disconnect ${row.client}${who}? It stops working straight away.`)) return
    try {
      await api.del(`/api/oauth/connections/${row.id}`)
      qc.invalidateQueries({ queryKey: ['oauth-connections'] })
    } catch (err) {
      toast.error(err)
    }
  }

  if (!rows.length && onEmpty !== null) return onEmpty

  return (
    <div className="card">
      <CardHead
        title={tenantWide ? 'Connections in this company' : 'Your connections'}
        sub={
          tenantWide
            ? 'Every live connection here, and who made it.'
            : 'Assistants you have connected to your own account.'
        }
      />
      {rows.length === 0 ? (
        <p className="sub connections-empty">
          <Cable size={15} /> Nothing is connected yet.
        </p>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Application</th>
              {tenantWide && <th>Person</th>}
              <th>Access</th>
              <th>Connected</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.client}</strong>
                </td>
                {tenantWide && <td>{c.userName || '—'}</td>}
                <td>
                  {/* The words a person agreed to on the consent page, not the scope strings. */}
                  {c.scope.includes('board:write') ? (
                    <span className="tag">Read and change</span>
                  ) : (
                    <span className="tag muted">Read only</span>
                  )}
                </td>
                <td className="cell-quiet">{new Date(c.connectedAt).toLocaleDateString()}</td>
                <td className="cell-quiet">
                  {c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : 'never'}
                </td>
                <td>
                  <button className="btn sm danger" onClick={() => disconnect(c)}>
                    Disconnect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── The owner's panel ───────────────────────────────────────────────────────

/**
 * One card per integration: the switch, what it means, and every connection anyone here has
 * made. Owner-only, and guarded on the route as well as here.
 */
export function ManageIntegrations() {
  const list = useIntegrations()
  // One tab per integration, so each is managed on its own terms rather than as a row in a
  // list of switches. Local state rather than a URL segment: /settings/integrations/manage
  // is one page, and which tab is open is not a place anybody links to.
  const [openKey, setOpenKey] = useState(list[0]?.key)
  const open = list.find((i) => i.key === openKey) || list[0]

  return (
    <>
      <BackToIntegrations />
      <div className="card">
        <CardHead
          title="Manage integrations"
          sub="Turn integrations on or off, and disconnect anyone using them."
        />
        <p className="sub manage-note">
          Turning off a connector will block everyone from using it, but existing connections
          will work if you toggle it back on, so nobody has to reconnect.
        </p>
        <div className="segmented manage-tabs">
          {list.map((i) => (
            <button
              key={i.key}
              className={i.key === open?.key ? 'selected' : ''}
              aria-current={i.key === open?.key ? 'true' : undefined}
              onClick={() => setOpenKey(i.key)}
            >
              <Marks marks={i.marks} small />
              {i.name}
              {i.comingSoon && <span className="tag muted">Soon</span>}
              {!i.comingSoon && !i.on && <span className="tag muted">Disabled</span>}
            </button>
          ))}
        </div>
      </div>

      {open && (open.Manage ? <open.Manage integration={open} /> : <ComingSoonCard integration={open} />)}
    </>
  )
}

/**
 * An integration with nothing to manage yet. It appears here rather than being dropped from
 * the strip because an owner who has just seen it on the grid will come looking, and a
 * switch that is present and disabled answers them; an absent one just looks like a bug.
 */
function ComingSoonCard({ integration }) {
  return (
    <div className="card">
      <CardHead
        title={
          <span className="card-title-marks">
            <Marks marks={integration.marks} small />
            {integration.name}
          </span>
        }
        sub={integration.blurb}
        action={<Switch checked={false} disabled onChange={() => {}} />}
      />
      <div className="notice notice-info">
        <Info size={16} />
        <span>
          Not available yet. There is nothing to turn on and nothing to manage — this is here
          so you know it is coming.
        </span>
      </div>
    </div>
  )
}

function McpManage({ integration }) {
  return (
    <>
      <IntegrationSwitchCard
        integration={integration}
        notice={
          <>
            Anyone can connect an assistant to their own account. The assistant can only view
            and modify what that user has access to, and every change is recorded in the audit
            log under their name.
          </>
        }
      >
        <SettingsRow
          label="Disconnect everyone"
          hint="Forces everyone to reconnect."
        >
          <DisconnectEveryone disabled={!integration.on} />
        </SettingsRow>
      </IntegrationSwitchCard>

      {integration.on && (
        <Connections
          scope="tenant"
          onEmpty={
            <div className="card">
              <p className="sub connections-empty">
                <Cable size={15} /> Nobody has connected anything yet.
              </p>
            </div>
          }
        />
      )}
    </>
  )
}

/** The switch itself, shared by every integration's manage card. */
function IntegrationSwitchCard({ integration, notice, children }) {
  const toast = useToast()
  const qc = useQueryClient()
  const { refresh } = useAuth()
  const [busy, setBusy] = useState(false)

  async function set(on) {
    setBusy(true)
    try {
      await api.put('/api/admin/settings', { [integration.setting]: on })
      // The switch lives in the session payload, so the tile grid and this card pick the
      // change up from the same refresh rather than waiting for a reload.
      refresh()
      qc.invalidateQueries({ queryKey: ['oauth-connections'] })
    } catch (err) {
      toast.error(err)
      refresh()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <CardHead
        title={integration.name}
        sub={integration.blurb}
        action={<Switch checked={integration.on} onChange={set} disabled={busy} />}
      />
      {notice && (
        <div className="notice notice-info">
          <Info size={16} />
          <span>{notice}</span>
        </div>
      )}
      <div className="settings-list">{children}</div>
    </div>
  )
}

function DisconnectEveryone({ disabled }) {
  const toast = useToast()
  const qc = useQueryClient()

  async function go() {
    if (!window.confirm('Disconnect every connection in the company? Each person would have to connect again.')) return
    try {
      const res = await api.post('/api/oauth/disconnect-all')
      toast.success(`Disconnected ${res.revoked} connection${res.revoked === 1 ? '' : 's'}.`)
      qc.invalidateQueries({ queryKey: ['oauth-connections'] })
    } catch (err) {
      toast.error(err)
    }
  }

  return (
    <button className="btn sm danger" disabled={disabled} onClick={go}>
      Disconnect everyone
    </button>
  )
}
